import React, {useEffect, useMemo, useState} from 'react';
import {Image, VStack, useTheme, Link, Flex, Box} from '@chakra-ui/react';
import {Offer, Event} from '../../../../types/article';
import {TextA18, TextG12, H2, TextG14, TextG18, TextG20} from '../../../typography';
import Modal from '../../../Modals/Modal';
import {has} from 'lodash';
import Icon from '../../../Icon';
import OfferImage from '../../../../assets/images/offerImage.png';
import {useNavigate} from 'react-router-dom';
import {OfferType} from '../../../../types/article';
import PointBoostModalContent from './PointBoostModalContent';
import {getImageURL} from '../../../../constants/imageStorage';
import RedemptionModalContent from './RedemptionModalContent';
import {calculateDistance, getDistanceString} from '../../../../utils/distance';
import {useGeneralSettings} from '../../../../hooks/useGeneralSettings';
import useGetUserLocation from '../../../../hooks/useUserLocation';
import {ActiveRedemption} from '../../../../hooks/useActiveRedemptions';
import dayjs from 'dayjs';
import useTrackEvent from '../../../../hooks/useTrackEvent';
import {isFutureOffer} from '../../../../utils/date';
import useBookmark from '../../../../hooks/useBookmark';
import {useFetchEvent} from '../../../../hooks/useEvent';
import {checkIsOfferLocked} from '../OfferCard';
import {storeScrollPositionInSession} from '../../../../utils/scroll';
import SignUpRequiredWrapper from '../../../SignupRequiredModal/SignupRequiredWrapper';
import SignupRequiredModal from '../../../SignupRequiredModal';
import useAuth from '../../../../hooks/useAuth';
import BookmarkButton from '../../../BookmarkButton';
import {UsersListModal} from '../../../UsersList';
import {useFetchOfferBookmarkedUsers} from '../../../../hooks/useOffers';

const OfferModal = ({
  offer,
  showModal,
  setShowModal,
  bookmarkHandler,
  activeRedemption,
  isPreview = false,
}: {
  offer: Offer;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  bookmarkHandler?: (e: any) => Promise<string | number | undefined> | undefined;
  activeRedemption?: ActiveRedemption;
  isPreview?: boolean;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {getSetting, isLoading} = useGeneralSettings(!isPreview);
  const userLocation = useGetUserLocation(!isPreview);
  const datePassed = useMemo(() => dayjs().utc().isAfter(dayjs.utc(offer?.ends_at)), [offer]);
  const {addUserEvent} = useTrackEvent();
  const futureOffer = useMemo(() => isFutureOffer(offer), [offer]);
  const { isBookmarked, handleOnBookmarkClick } = useBookmark(offer as Offer, 'offer');
  const place = offer.place;
  const event = offer.event;
  const bookmarkHandle = bookmarkHandler ? bookmarkHandler : handleOnBookmarkClick;
  const [signUpRequiredOpen, setSignUpRequiredOpen] = useState(false);
  const {user} = useAuth();
  const [showUsersModal, setShowUsersModal] = useState(false);
  const bookmarksCount = offer?.bookmarks_count || 0;

  const [offerEvent, setOfferEvent] = useState<Event | undefined>(event);
  const {data: lazyLoadadedEvent} = useFetchEvent(offer.event_id, !isPreview);

  // Determine the effective place - use offer's place first, then event's place as fallback
  const effectivePlace = useMemo(() => {
    if (place) return place;
    if (offerEvent?.place) return offerEvent.place;
    if (event?.place) return event.place;
    return null;
  }, [place, offerEvent, event]);

  const onSignupClicked = () => {
    if (user) {
      navigate('/download');
    } else {
      navigate('/register');
    }
    setShowModal(false);
  };

  useEffect(() => {
    if (
      (!offerEvent ||
        (offerEvent && has(offerEvent, 'unlocks_offer_id') && has(offerEvent, 'unlocked_offer'))) &&
      lazyLoadadedEvent
    ) {
      const fetchedOffer = lazyLoadadedEvent?.data;
      setOfferEvent(fetchedOffer);
    }
  }, [lazyLoadadedEvent]);

  useEffect(() => {
    if (showModal) {
      addUserEvent({
        type: 'modal_view',
        event_name: 'view_offer',
        event_time: dayjs().format(),
        data: {offerId: offer.id.toString()},
      });
    }
  }, [showModal]);

  // Check if offer is locked behind membership
  const lockInfo = useMemo(() => {
    return checkIsOfferLocked(offer.id, offerEvent, offer, user);
  }, [offer.id, offerEvent, offer, user]);

  const iconStyle: React.CSSProperties = React.useMemo(
    () => ({
      color: theme.colors.lightBlue,
      fontSize: '1.5rem',
      position: 'absolute',
      top: '10px',
      right: '10px',
    }),
    [theme]
  );
  const distance = useMemo(() => {
    const placeToUse = effectivePlace || place;
    if (offer.place || placeToUse) {
      return calculateDistance(
        {
          latitude: Number(userLocation.data?.latitude),
          longitude: Number(userLocation.data?.longitude),
        },
        {
          latitude: Number(offer?.place?.latitude || placeToUse?.latitude),
          longitude: Number(offer?.place?.longitude || placeToUse?.longitude),
        }
      );
    }
    return 0;
  }, [userLocation, place, offer.place, effectivePlace]);  const offerContent = useMemo(() => {
    // Use effectivePlace if available, otherwise fallback to place
    const placeToPass = effectivePlace || place;
    
    switch (offer.offer_type) {
      case OfferType.POINTS_BOOST:
        return (
          <PointBoostModalContent
            setShowModal={setShowModal}
            isBookmarked={isBookmarked}
            bookmarkHandler={bookmarkHandle}
            isFutureOffer={futureOffer}
            offer={offer}
            place={placeToPass}
            {...(offerEvent && {event: offerEvent})}
            datePassed={datePassed}
            isPreview={isPreview}
          />
        );
      case OfferType.REDEMPTION:
      case OfferType.PREMIUM_REDEMPTION:
      case OfferType.INDIVIDUAL_REDEMPTION:
      case OfferType.EVENT_REDEMPTION:
      default:
        return (
          <RedemptionModalContent
            isFutureOffer={futureOffer}
            offer={offer}
            {...(offerEvent && {event: offerEvent})}
            place={placeToPass}
            setShowModal={setShowModal}
            isBookmarked={isBookmarked}
            bookmarkHandler={bookmarkHandle}
            activeRedemption={activeRedemption}
            datePassed={datePassed}
            isPreview={isPreview}
          />
        );
    }
  }, [offer, setShowModal, isBookmarked, bookmarkHandler, offerEvent, effectivePlace, place]);return (
    <>
    <Modal
      parentCallback={() => setShowModal(false)}
      closeButton={true}
      closeButtonPosition="left"
      //contentStyle={{maxHeight: screen.height * 0.95, overflow: 'scroll'}}
      header={
        <Box position="relative" w="100%">
          {!activeRedemption && !isPreview && (
            <BookmarkButton
              isBookmarked={isBookmarked}
              onClick={bookmarkHandle}
              size="32px"
              iconSize="12px"
              position="absolute"
              top={-4}
              right={-3}
              zIndex={1}
            />
          )}
          <VStack mt={4}>
          {event && event.unlocks_offer_id === offer.id ? (
            <>
              <TextA18 align="center" fontWeight="700">
                {event.title}
              </TextA18>
              {!isNaN(distance) && (
                <TextG12 fontWeight="500" color={theme.colors.grey}>
                  <i
                    style={{marginRight: '5px', color: theme.colors.blue}}
                    className="fi fi-rr-marker"></i>
                  {getSetting('checkin_distance') && distance < getSetting('checkin_distance')
                    ? `You're Here!`
                    : `${getDistanceString(distance)} away`}
                </TextG12>
              )}              <Flex gap={2} mt={2}>
                {effectivePlace && (
                  <TextG12
                    onClick={() => {
                      if (!isPreview) {
                        storeScrollPositionInSession();
                        navigate(`/place/${effectivePlace.id}`);
                        setShowModal(false);
                      }
                    }}
                    textDecoration="underline"
                    fontWeight="500"
                    color={theme.colors.blue}
                    cursor="pointer">
                    See Place
                  </TextG12>
                )}
                <TextG12
                  onClick={() => {
                    if (!isPreview) {
                      storeScrollPositionInSession();
                      navigate(`/event/${event.id}`);
                      setShowModal(false);
                    }
                  }}
                  textDecoration="underline"
                  fontWeight="500"
                  color={theme.colors.blue}
                  cursor="pointer">
                  See Event
                </TextG12>
                {bookmarksCount > 0 && (
                  <TextG12
                    onClick={() => setShowUsersModal(true)}
                    textDecoration="underline"
                    fontWeight="500"
                    color={theme.colors.blue}
                    cursor="pointer">
                    {`${bookmarksCount} Saved`}
                  </TextG12>
                )}
              </Flex>
            </>
          ) : place && place.name ? (
            <>
              <TextA18 align="center" fontWeight="700">
                {place.name}
              </TextA18>
              {!isNaN(distance) && (
                <TextG12 fontWeight="500" color={theme.colors.grey}>
                  <i
                    style={{marginRight: '5px', color: theme.colors.blue}}
                    className="fi fi-rr-marker"></i>
                  {getSetting('checkin_distance') && distance < getSetting('checkin_distance')
                    ? `You're Here!`
                    : `${getDistanceString(distance)} away`}
                </TextG12>
              )}              <Flex gap={2} mt={2}>
                <TextG12
                  onClick={() => {
                    if (!isPreview) {
                      storeScrollPositionInSession();
                      navigate(`/place/${place.id}`);
                      setShowModal(false);
                    }
                  }}
                  textDecoration="underline"
                  fontWeight="500"
                  color={theme.colors.blue}
                  cursor="pointer">
                  See Place
                </TextG12>
                {offerEvent && (
                  <TextG12
                    onClick={() => {
                      if (!isPreview) {
                        storeScrollPositionInSession();
                        navigate(`/event/${offerEvent.id}`);
                        setShowModal(false);
                      }
                    }}
                    textDecoration="underline"
                    fontWeight="500"
                    color={theme.colors.blue}
                    cursor="pointer">
                    See Event
                  </TextG12>
                )}
                {bookmarksCount > 0 && (
                  <TextG12
                    onClick={() => setShowUsersModal(true)}
                    textDecoration="underline"
                    fontWeight="500"
                    color={theme.colors.blue}
                    cursor="pointer">
                    {`${bookmarksCount} Saved`}
                  </TextG12>
                )}
              </Flex>
            </>
          ) : (            <>
              {/* Case where neither event unlock nor place name, but we might still have event or place */}
              {(offerEvent || effectivePlace || bookmarksCount > 0) && (
                <Flex gap={2} mt={2}>
                  {effectivePlace && (
                    <TextG12
                      onClick={() => {
                        if (!isPreview) {
                          storeScrollPositionInSession();
                          navigate(`/place/${effectivePlace.id}`);
                          setShowModal(false);
                        }
                      }}
                      textDecoration="underline"
                      fontWeight="500"
                      color={theme.colors.blue}
                      cursor="pointer">
                      See Place
                    </TextG12>
                  )}
                  {offerEvent && (
                    <TextG12
                      onClick={() => {
                        if (!isPreview) {
                          storeScrollPositionInSession();
                          navigate(`/event/${offerEvent.id}`);
                          setShowModal(false);
                        }
                      }}
                      textDecoration="underline"
                      fontWeight="500"
                      color={theme.colors.blue}
                      cursor="pointer">
                      See Event
                    </TextG12>
                  )}
                  {bookmarksCount > 0 && (
                    <TextG12
                      onClick={() => setShowUsersModal(true)}
                      textDecoration="underline"
                      fontWeight="500"
                      color={theme.colors.blue}
                      cursor="pointer">
                      {`${bookmarksCount} Saved`}
                    </TextG12>
                  )}
                </Flex>
              )}
            </>
          )}
        </VStack>
        </Box>
      }
      show={showModal}
      children={
        isPreview ? (
          <SignUpRequiredWrapper setSignUpRequiredOpen={setSignUpRequiredOpen}>
            <SignupRequiredModal
              isOpen={signUpRequiredOpen}
              setIsOpen={setSignUpRequiredOpen}
              onButtonClicked={onSignupClicked}
            />
            <VStack mt={-2} mb={2} gap={1}>
              {offer.title && (
                <H2
                  color={theme.colors.rose}
                  dangerouslySetInnerHTML={{__html: offer?.title}}
                  mb={3}
                  align="center"
                  maxW="240px"
                  fontWeight="700">
                  {}
                </H2>
              )}
              <Box position="relative" display="inline-block">
                <Image
                  fallbackStrategy="onError"
                  mt={'0 !important'}
                  borderRadius="14px"
                  width="184px"
                  height="136px"
                  objectFit="cover"
                  src={offer?.image_url ? getImageURL(offer.image_url) : OfferImage}
                  // onClick={() => {
                  //   storeScrollPositionInSession();
                  //   offer.place || place ? navigate(`/place/${offer.place?.id || place.id}`) : null;
                  //   setShowModal(false);
                  // }}
                />
                {lockInfo.lockType === 'membership' && (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    background="rgba(0, 0, 0, 0.5)"
                    zIndex={1}
                    borderRadius="14px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer">
                    <VStack spacing={1}>
                      <Icon iconName="fi fi-sr-lock" style={{color: 'white', fontSize: '24px'}} />
                      <TextG18 color="white" fontWeight="700" textAlign="center" lineHeight="1.2">
                        Special Perk for Insiders
                      </TextG18>
                      <TextG18 color="white" fontWeight="400" textAlign="center" lineHeight="1.1">
                        Insiders use perks as a reason to get together out in the neighborhood.
                      </TextG18>
                      <TextG18 color="white" fontWeight="400" textAlign="center" lineHeight="1.1">
                        Join the Insiders Club to unlock even more connection.
                      </TextG18>
                    </VStack>
                  </Box>
                )}
              </Box>

              {offerContent}
            </VStack>
          </SignUpRequiredWrapper>
        ) : (
          <VStack mt={-2} mb={2} gap={1}>
            {offer.title && (
              <H2
                color={theme.colors.rose}
                dangerouslySetInnerHTML={{__html: offer?.title}}
                mb={3}
                align="center"
                maxW="240px"
                fontWeight="700">
                {}
              </H2>
            )}
            <Box position="relative" display="inline-block">
              <Image
                fallbackStrategy="onError"
                mt={'0 !important'}
                borderRadius="14px"
                width="184px"
                height="136px"
                objectFit="cover"
                src={offer?.image_url ? getImageURL(offer.image_url) : OfferImage}
                // onClick={() => {
                //   storeScrollPositionInSession();
                //   offer.place || place ? navigate(`/place/${offer.place?.id || place.id}`) : null;
                //   setShowModal(false);
                // }}
              />
              {lockInfo.lockType === 'membership' && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  background="rgba(0, 0, 0, 0.5)"
                  zIndex={1}
                  borderRadius="14px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer">
                  <VStack spacing={1}>
                    <Icon iconName="fi fi-sr-lock" style={{color: 'white', fontSize: '24px'}} />
                    <TextG18 color="white" fontWeight="700" textAlign="center" lineHeight="1.2">
                      Special Perk for Insiders
                    </TextG18>
                    <TextG18 color="white" fontWeight="400" textAlign="center" lineHeight="1.1">
                      Insiders use perks as a reason to get together out in the neighborhood.
                    </TextG18>
                    <TextG18 color="white" fontWeight="400" textAlign="center" lineHeight="1.1">
                      Join the Insiders Club to unlock even more connection.
                    </TextG18>
                  </VStack>
                </Box>
              )}
            </Box>

            {offerContent}
          </VStack>
        )
      }
    />
    <UsersListModal
      isOpen={showUsersModal}
      onClose={() => setShowUsersModal(false)}
      title="Neighbors Interested"
      totalCount={bookmarksCount}
      fetchUsersHook={useFetchOfferBookmarkedUsers}
      entityId={offer.id}
    />
  </>
  );
};

export default OfferModal;
